'use client';

import { useRef, type ReactNode } from 'react';
import {
  bindOnceTextReveal,
  TEXT_REVEAL_FULL,
  TEXT_REVEAL_SUBTLE,
} from 'components/landing/v3/scrollBeat';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import styles from './TextReveal.module.css';

export function HowItWorksTextReveal({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const gsapCtx = useGsapContext(rootRef);

  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    return gsapCtx.add(() => {
      const unbinds: Array<() => void> = [];
      const bound = new Set<HTMLElement>();

      const bindVisible = () => {
        root
          .querySelectorAll<HTMLElement>('[data-hiw-reveal-group]')
          .forEach((group) => {
            if (bound.has(group) || !group.getClientRects().length) return;
            bound.add(group);

            const full = [
              ...group.querySelectorAll<HTMLElement>('[data-hiw-reveal="full"]'),
            ].filter((el) => el.getClientRects().length);
            const subtle = [
              ...group.querySelectorAll<HTMLElement>(
                '[data-hiw-reveal="subtle"]',
              ),
            ].filter((el) => el.getClientRects().length);

            if (full.length) {
              unbinds.push(
                bindOnceTextReveal(full, {
                  ...TEXT_REVEAL_FULL,
                  stagger: full.length > 1 ? 0.06 : 0,
                  trigger: group,
                }),
              );
            }
            if (subtle.length) {
              unbinds.push(
                bindOnceTextReveal(subtle, {
                  ...TEXT_REVEAL_SUBTLE,
                  delay: 0.08,
                  stagger: 0.06,
                  trigger: group,
                }),
              );
            }
          });

        root.querySelectorAll<HTMLElement>('[data-hiw-reveal]').forEach((el) => {
          if (
            el.closest('[data-hiw-reveal-group]') ||
            bound.has(el) ||
            !el.getClientRects().length
          ) {
            return;
          }
          bound.add(el);
          const subtle = el.getAttribute('data-hiw-reveal') === 'subtle';
          unbinds.push(
            bindOnceTextReveal(
              el,
              subtle ? { ...TEXT_REVEAL_SUBTLE } : { ...TEXT_REVEAL_FULL },
            ),
          );
        });
      };

      bindVisible();
      const desktop = window.matchMedia('(min-width: 992px)');
      desktop.addEventListener('change', bindVisible);

      return () => {
        desktop.removeEventListener('change', bindVisible);
        unbinds.forEach((unbind) => unbind());
      };
    });
  }, [gsapCtx]);

  return (
    <div ref={rootRef} className={styles.root}>
      {children}
    </div>
  );
}
